"use client";

import { User, CheckCircle2, Clock, Send, FileCheck } from "lucide-react";

interface InvestmentCardMiniWidgetProps {
  className?: string;
}

// Document status types matching the real app
type DocumentStatus = "pending" | "sent" | "signed" | "approved";

interface DocumentBadge {
  type: string;
  label: string;
  status: DocumentStatus;
}

// Status icon and color configuration matching the real KanbanView
const statusConfig: Record<
  DocumentStatus,
  {
    icon: React.ReactNode;
    iconColor?: string;
  }
> = {
  pending: {
    icon: <Clock size={10} strokeWidth={2.5} />,
  },
  sent: {
    icon: <Send size={10} strokeWidth={2.5} />,
  },
  signed: {
    icon: <FileCheck size={10} strokeWidth={2.5} />,
    iconColor: "text-green-600",
  },
  approved: {
    icon: <CheckCircle2 size={10} strokeWidth={2.5} />,
    iconColor: "text-green-600",
  },
};

// Status labels in Spanish
const statusLabels: Record<DocumentStatus, string> = {
  pending: "Pendiente",
  sent: "Enviado",
  signed: "Firmado",
  approved: "Aprobado",
};

// Mock investment data - realistic for a film investment
const mockInvestment = {
  id: "INV-A7K9",
  promoterName: "Aurora Films",
  investorName: "Carlos Mendez",
  investorType: "individual" as const,
  amount: 35000,
  returnPercentage: 5.8,
  state: "funded" as const,
  relativeTime: "Hace 2d",
};

// Mock document badges - the KEY element of this widget
// Showing a mix of statuses to make it interesting and realistic
const mockDocumentBadges: DocumentBadge[] = [
  { type: "financing", label: "Financiacion", status: "approved" },
  { type: "payment", label: "Pago", status: "approved" },
  { type: "communication", label: "Comunicacion", status: "signed" },
  { type: "tax_certificate", label: "Justificante", status: "pending" },
];

// State badge configuration (funded state)
const stateBadgeConfig = {
  funded: {
    bg: "rgba(16, 185, 129, 0.1)",
    text: "#059669",
    dot: "#10b981",
    label: "Financiado",
  },
};

// Type badge configuration
const typeBadgeConfig = {
  individual: {
    bg: "rgba(245, 158, 11, 0.1)",
    text: "#f59e0b",
    label: "Autonomo",
  },
  company: {
    bg: "rgba(59, 130, 246, 0.1)",
    text: "#3b82f6",
    label: "Empresa",
  },
};

export function InvestmentCardMiniWidget({ className }: InvestmentCardMiniWidgetProps) {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const stateConfig = stateBadgeConfig.funded;
  const typeConfig = typeBadgeConfig[mockInvestment.investorType];

  const renderDocumentBadge = (badge: DocumentBadge) => {
    const config = statusConfig[badge.status];
    return (
      <span
        key={badge.type}
        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-50 text-gray-600"
      >
        <span className={config.iconColor || ""}>{config.icon}</span>
        <span>
          {badge.label} {statusLabels[badge.status]}
        </span>
      </span>
    );
  };

  return (
    <div className={className}>
      {/* Card Container - compact vertical layout for horizontal scroll */}
      <div className="w-56 bg-white rounded-lg border border-zinc-200 shadow-sm overflow-hidden">
        {/* Card Content - matching the real KanbanView card structure */}
        <div className="p-3">
          {/* Row 1: Promoter Name */}
          <h4 className="text-[12px] font-semibold text-zinc-900 truncate mb-1">
            {mockInvestment.promoterName}
          </h4>

          {/* Row 2: Investor Icon + Name */}
          <div className="flex items-center gap-1 mb-2">
            <User size={11} className="text-zinc-400" strokeWidth={2} />
            <span className="text-[11px] text-zinc-600 truncate">
              {mockInvestment.investorName}
            </span>
          </div>

          {/* Row 3: Amount */}
          <div className="text-base font-bold text-zinc-900">
            {formatCurrency(mockInvestment.amount)}
          </div>

          {/* Row 4: Return Percentage */}
          <div className="text-[11px] text-zinc-500 mb-2">
            Rentabilidad {mockInvestment.returnPercentage.toFixed(1)}%
          </div>

          {/* Row 5: State and Type Badges */}
          <div className="flex items-center gap-1.5 flex-wrap mb-3">
            {/* Status Badge with bar variant */}
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold"
              style={{ backgroundColor: stateConfig.bg, color: stateConfig.text }}
            >
              <span
                className="w-0.5 h-2.5 rounded-full mr-1"
                style={{ backgroundColor: stateConfig.dot }}
              />
              {stateConfig.label}
            </span>

            {/* Type Badge */}
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium"
              style={{ backgroundColor: typeConfig.bg, color: typeConfig.text }}
            >
              {typeConfig.label}
            </span>
          </div>

          {/* Row 6: Document Status Badges - THE HERO ELEMENT */}
          <div className="space-y-1.5 pt-2 border-t border-zinc-100">
            <div className="text-[9px] font-medium text-zinc-400 uppercase tracking-wider mb-1.5">
              Estado
            </div>
            <div className="flex flex-wrap gap-1">
              {mockDocumentBadges.map((badge) => renderDocumentBadge(badge))}
            </div>
          </div>

          {/* Row 7: Footer */}
          <div className="flex items-center justify-between text-[10px] text-zinc-400 pt-2.5 mt-2.5 border-t border-zinc-100">
            <span className="font-mono">#{mockInvestment.id}</span>
            <span>{mockInvestment.relativeTime}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvestmentCardMiniWidget;
